/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  type AttributeDataTypeType,
  FormSchemaQuestionType,
  type FormSchemaType,
  isNormalFormQuestionLookup,
} from "@validereinc/domain";
import {
  type RecordValueType,
  toFlattenedObject,
} from "@validereinc/utilities";
import { get, isEmpty, isEqual } from "es-toolkit/compat";
import React, { type ReactNode, useEffect, useMemo } from "react";
import { type UseFormReturn } from "react-hook-form";
import {
  getValuePathFromQuestionPath,
  type WatchedFields,
} from "../../../logic/forms/index.ts";
import { usePrevious } from "../../../utilities.ts";
import { FormSubmissionsSectionRepeater } from "./FormSubmissionsSectionRepeater.tsx";

export type SectionRendererPropType = {
  onAddSection: () => void;
  onRemoveSection: () => void;
  index: number;
  title: string;
  addButtonLabel: string;
  hasErrors?: boolean;
  canAddSection?: boolean;
  canRemoveSection?: boolean;
  children: ReactNode;
  dataKey: string;
  description?: string;
};

export type QuestionRendererPropType = {
  dataKey: string;
  name: string;
  data_type?: AttributeDataTypeType;
} & FormSchemaQuestionType &
  Partial<RecordValueType<WatchedFields>["calculation"]> &
  Partial<{
    questionsMap: FormSchemaType["config"]["questions"];
  }> &
  Partial<{
    measurement_type: string;
    measurement_unit: string;
  }>;

export type SectionRenderFunctionType = (
  sectionRenderFunctionProps: SectionRendererPropType
) => ReactNode;

export type QuestionRenderFunctionType = (
  questionRenderFunctionProps: QuestionRendererPropType
) => ReactNode;

export type FormSubmissionFormControllerType = (props: {
  form: UseFormReturn<any>;
  formSchema?: FormSchemaType;
  sectionRenderFunction: SectionRenderFunctionType;
  questionRenderFunction: QuestionRenderFunctionType;
  defaultAnswers?: any;
}) => JSX.Element | null;

const FormSubmissionFormController: FormSubmissionFormControllerType = ({
  form,
  formSchema,
  sectionRenderFunction,
  questionRenderFunction,
  defaultAnswers,
}) => {
  const formValues = form.watch();
  const formValuesSerialized = JSON.stringify(formValues);
  const previousFormValuesSerialized = usePrevious(formValuesSerialized);

  /**
   * This object has the form of: {parentQuestionId: ["dependentQuestionId1", "dependentQuestionId2", ... ], ...}
   * "dependent" here, is a lookup field that it's value gets wiped when its parent value is changed.
   */
  const questionDependencyMap = useMemo(() => {
    if (!formSchema) return {};

    const dependencies: Record<string, string[]> = {};

    Object.entries(formSchema.config.questions).map(
      ([questionId, questionDefinition]) => {
        if (
          questionDefinition.type === "question" &&
          isNormalFormQuestionLookup(questionDefinition)
        ) {
          if (questionDefinition?.filter) {
            Object.values(toFlattenedObject(questionDefinition.filter)).forEach(
              (filterValue) => {
                if (
                  typeof filterValue === "string" &&
                  filterValue?.startsWith("%") &&
                  filterValue?.endsWith("%")
                ) {
                  const parentQuestionId = filterValue.slice(1, -1);

                  if (dependencies?.[parentQuestionId]) {
                    dependencies[parentQuestionId].push(questionId);
                  } else {
                    dependencies[parentQuestionId] = [questionId];
                  }
                }
              }
            );
          } else if (questionDefinition?.parent_facility_question_id) {
            const parentQuestionId =
              questionDefinition?.parent_facility_question_id;
            if (dependencies?.[parentQuestionId]) {
              dependencies[parentQuestionId].push(questionId);
            } else {
              dependencies[parentQuestionId] = [questionId];
            }
          }
        }
      }
    );
    return dependencies;
  }, [formSchema]);

  /**
   * This object has the form of: {"answers.sectionId.sectionIndex.parentQuestionId": ["answers.sectionId.sectionIndex.dependentQuestionId1", ... ], ...}
   * So it's the same as questionDependencyMap, but with full paths that represent form answers.
   */
  const fieldsToWatch = useMemo(() => {
    const dependencies: Record<string, string[]> = {};

    formSchema?.config.sections.forEach((section) => {
      if (isEmpty(formValues)) return;
      for (
        let sectionIndex = 0;
        sectionIndex < (formValues.answers[section.id]?.length ?? 0);
        sectionIndex++
      ) {
        Object.entries(questionDependencyMap).forEach(
          ([parentQuestionId, dependentQuestionIds]) => {
            const listenTo = `answers.${section.id}.${sectionIndex}.${parentQuestionId}`;
            dependentQuestionIds.forEach((dependentQuestionId) => {
              const dependentFullPath = `answers.${section.id}.${sectionIndex}.${dependentQuestionId}`;
              if (dependencies?.[listenTo]) {
                dependencies[listenTo].push(dependentFullPath);
              } else {
                dependencies[listenTo] = [dependentFullPath];
              }
            });
          }
        );
      }
    });

    return dependencies;
  }, [questionDependencyMap, formValuesSerialized]);

  useEffect(() => {
    // Guard initial mount: skip clearing dependents when the form is first hydrated
    if (previousFormValuesSerialized === undefined) return;

    const previousFormValues = JSON.parse(previousFormValuesSerialized);

    Object.keys(fieldsToWatch).forEach((fieldKey) => {
      const currentValue = get(
        formValues,
        getValuePathFromQuestionPath(fieldKey)
      );
      const previousValue = get(
        previousFormValues,
        getValuePathFromQuestionPath(fieldKey)
      );
      const parentIsDirty = get(
        form.formState.dirtyFields,
        getValuePathFromQuestionPath(fieldKey)
      );
      if (
        currentValue &&
        !isEqual(previousValue, currentValue) &&
        parentIsDirty
      ) {
        fieldsToWatch[fieldKey].forEach((dependentAnswerPath) => {
          form.setValue(getValuePathFromQuestionPath(dependentAnswerPath), "");
        });
      }
    });
  }, [
    JSON.stringify(fieldsToWatch),
    formValuesSerialized,
    previousFormValuesSerialized,
    JSON.stringify(form.formState.dirtyFields),
  ]);

  if (
    !formSchema ||
    !Array.isArray(formSchema?.config?.sections) ||
    isEmpty(formSchema?.config?.questions ?? {})
  )
    return null;

  return (
    <>
      {formSchema.config.sections.map((sectionProps) => (
        <FormSubmissionsSectionRepeater
          key={sectionProps.id}
          sectionRenderFunction={sectionRenderFunction}
          questionRenderFunction={questionRenderFunction}
          formQuestions={formSchema?.config?.questions}
          form={form}
          defaultAnswers={defaultAnswers}
          {...sectionProps}
        />
      ))}
    </>
  );
};

export default FormSubmissionFormController;
